// MARK://dhk8/admin-shell/pages/modules/Warehouses
// Listado de almacenes (warehouses) → dhk8-erp-inventory.
// BUG prod 2026-06-15: los almacenes se podían CREAR (modal en Inventario) pero
// no había página para VERLOS listados ni link en el nav. Esta página los lista
// y permite crear (mismo cliente que usa InventoryPage: listWarehouses/createWarehouse).
//
// DS RBAC (Gustavo 2026-06-22): la lista usa DataTable + FilterBar + StatusPill(active).
// Clic en fila → /inventory/warehouses/:id (drill-in). Editar/eliminar el almacén
// EN SÍ (nombre/código/activo) vive en la ficha de detalle (WarehouseDetailPage),
// ya cableado a updateWarehouse/deleteWarehouse del subgrafo erp-inventory.
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Warehouse as WarehouseIcon } from 'lucide-react'
import * as inv from '../../lib/clients/inventory.js'
import { useAsync, asyncState } from '../../lib/use-async.js'
import Sheet from '../../components/Sheet.js'
import DataTable, { type DataTableColumn, type SortState } from '../../components/DataTable.js'
import FilterBar from '../../components/FilterBar.js'
import StatusPill, { lifecycleTone } from '../../components/StatusPill.js'
import { useToast } from '../../components/Toast.js'

export default function WarehousesPage() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const warehouses = useAsync((s) => inv.listWarehouses(s))

  const [creating, setCreating] = useState(false)
  const [form, setForm] = useState<{ name: string; code: string }>({ name: '', code: '' })
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const [search, setSearch] = useState('')
  const [activeFilter, setActiveFilter] = useState('')
  const [sort, setSort] = useState<SortState | undefined>(undefined)

  async function save() {
    setError(null)
    if (!form.name.trim()) { setError('El nombre es obligatorio.'); return }
    setSaving(true)
    try {
      await inv.createWarehouse({ name: form.name.trim(), ...(form.code.trim() ? { code: form.code.trim() } : {}) })
      setCreating(false); setForm({ name: '', code: '' }); warehouses.reload(); toast('Almacén creado', 'success')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo crear el almacén.')
    } finally { setSaving(false) }
  }

  const rows = useMemo(() => {
    let r = warehouses.data ?? []
    const q = search.trim().toLowerCase()
    if (q) r = r.filter((w) => w.name.toLowerCase().includes(q) || (w.code ?? '').toLowerCase().includes(q))
    if (activeFilter) r = r.filter((w) => (activeFilter === 'active' ? w.active : !w.active))
    if (sort) {
      const dir = sort.dir === 'asc' ? 1 : -1
      r = r.slice().sort((a, b) => {
        let av: number | string, bv: number | string
        switch (sort.key) {
          case 'name': av = a.name.toLowerCase(); bv = b.name.toLowerCase(); break
          case 'code': av = (a.code ?? '').toLowerCase(); bv = (b.code ?? '').toLowerCase(); break
          case 'active': av = a.active ? 1 : 0; bv = b.active ? 1 : 0; break
          default: return 0
        }
        return av < bv ? -dir : av > bv ? dir : 0
      })
    }
    return r
  }, [warehouses.data, search, activeFilter, sort])

  const columns: DataTableColumn<inv.Warehouse>[] = [
    {
      key: 'name', header: 'Almacén', sortable: true, render: (w) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 'var(--pos-weight-medium, 500)' }}>
          <WarehouseIcon size={15} aria-hidden style={{ opacity: 0.65 }} />{w.name}
        </span>
      ),
    },
    { key: 'code', header: 'Código', sortable: true, render: (w) => w.code ? <span className="adm-mono">{w.code}</span> : <span style={{ color: 'var(--pos-fg-tertiary)' }}>—</span> },
    { key: 'active', header: 'Estado', sortable: true, render: (w) => <StatusPill tone={lifecycleTone(w.active)} label={w.active ? 'Activo' : 'Inactivo'} /> },
  ]

  return (
    <div className="adm-page">
      <div className="adm-page__head">
        <div><h1 className="adm-page__title">Almacenes</h1><p className="adm-page__sub">Ubicaciones donde se guarda el inventario</p></div>
        <div className="adm-page__actions">
          <button className="adm-btn adm-btn--primary" onClick={() => { setForm({ name: '', code: '' }); setError(null); setCreating(true) }}>
            <Plus size={16} /> Nuevo almacén
          </button>
        </div>
      </div>

      {asyncState(warehouses.loading, warehouses.error, warehouses.reload)}

      {warehouses.data && (
        <DataTable<inv.Warehouse>
          columns={columns}
          rows={rows}
          getRowKey={(w) => w.id}
          zebra
          sort={sort ?? null}
          onSortChange={setSort}
          onRowClick={(w) => navigate(`/inventory/warehouses/${w.id}`)}
          emptyText={warehouses.data.length === 0 ? 'Sin almacenes. Crea el primero para registrar inventario.' : 'Ningún almacén coincide con el filtro.'}
          toolbar={<FilterBar
            search={search}
            onSearch={setSearch}
            searchPlaceholder="Buscar almacén o código…"
            filters={[{ key: 'active', label: 'Estado', value: activeFilter, onChange: setActiveFilter, options: [{ value: '', label: 'Todos' }, { value: 'active', label: 'Activos' }, { value: 'inactive', label: 'Inactivos' }] }]}
          />}
        />
      )}

      <Sheet
        open={creating}
        title="Nuevo almacén"
        onClose={() => { setCreating(false); setError(null) }}
        footer={<>
          <button className="adm-btn" onClick={() => setCreating(false)}>Cancelar</button>
          <button className="adm-btn adm-btn--primary" onClick={save} disabled={saving || !form.name.trim()}>{saving ? 'Guardando…' : 'Guardar'}</button>
        </>}
      >
        {error && <div className="adm-banner adm-banner--danger" role="alert">{error}</div>}
        <label className="adm-field"><span className="adm-field__label">Nombre</span><input className="adm-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Almacén principal" /></label>
        <label className="adm-field"><span className="adm-field__label">Código (opcional)</span><input className="adm-input" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="MAIN" /></label>
      </Sheet>
    </div>
  )
}
